import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PushNotifications } from "@capacitor/push-notifications";
import { api } from "./api";
import { nativePlatform } from "./native";
import { useMe } from "./queries";

const STORAGE_KEY = "mr.nativePush";

/** The parent who turned notifications on here, and this install's token. */
interface Registration {
  token: string;
  ownerId: string;
}

function readRegistration(): Registration | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Registration) : null;
  } catch {
    return null;
  }
}

function writeRegistration(value: Registration | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Asks Apple or Google for this install's token. */
async function fetchToken(): Promise<string> {
  const handles: { remove: () => Promise<void> }[] = [];
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Couldn't reach the notification service. Try again.")), 20_000);
      void (async () => {
        handles.push(
          await PushNotifications.addListener("registration", (t) => {
            clearTimeout(timer);
            resolve(t.value);
          }),
          await PushNotifications.addListener("registrationError", (e) => {
            clearTimeout(timer);
            reject(new Error(e.error || "Couldn't turn on notifications"));
          }),
        );
        await PushNotifications.register();
      })().catch(reject);
    });
  } finally {
    await Promise.all(handles.map((h) => h.remove()));
  }
}

export const nativePushOwner = () => readRegistration()?.ownerId ?? null;

export async function enableNativePush(userId: string): Promise<"enabled" | "denied"> {
  let { receive } = await PushNotifications.checkPermissions();
  if (receive !== "granted") ({ receive } = await PushNotifications.requestPermissions());
  if (receive !== "granted") return "denied";
  const token = await fetchToken();
  await api.post("/push/devices", { platform: nativePlatform(), token });
  writeRegistration({ token, ownerId: userId });
  return "enabled";
}

/** Turn notifications off on this device. Call while still signed in, as logging out does. */
export async function disableNativePush(): Promise<void> {
  const registration = readRegistration();
  if (!registration) return;
  writeRegistration(null);
  await api.post("/push/devices/remove", { token: registration.token }).catch(() => {});
  await PushNotifications.unregister().catch(() => {});
}

/**
 * Keeps the server in step with who is using the phone: a parent's notifications stop while a kid or
 * another parent is signed in here (SA-19) and come back when that parent returns. A token can also
 * change between launches, so its owner re-sends it.
 */
async function syncRegistration(userId: string) {
  const registration = readRegistration();
  if (!registration) return;
  if (registration.ownerId !== userId) {
    await api.post("/push/devices/remove", { token: registration.token });
    return;
  }
  const { receive } = await PushNotifications.checkPermissions();
  if (receive !== "granted") return;
  const token = await fetchToken();
  if (token !== registration.token) {
    await api.post("/push/devices/remove", { token: registration.token });
    writeRegistration({ token, ownerId: userId });
  }
  await api.post("/push/devices", { platform: nativePlatform(), token });
}

/** Only pages inside the app: a notification's link never leads anywhere else. */
export function isAppPath(url: unknown): url is string {
  return typeof url === "string" && /^\/(?![/\\])/.test(url) && !url.includes("\\");
}

/** Render once inside the router, in the native apps only. */
export function NativePushBridge() {
  const navigate = useNavigate();
  const { data } = useMe();
  const userId = data?.user.id;

  useEffect(() => {
    if (userId) void syncRegistration(userId).catch((err) => console.warn("Push sync failed:", err));
  }, [userId]);

  // The plugin holds a tap that launched the app until this listener is added.
  useEffect(() => {
    const handle = PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
      const url = (notification.data as { url?: unknown } | undefined)?.url;
      if (isAppPath(url)) void navigate(url);
    });
    return () => {
      void handle.then((h) => h.remove());
    };
  }, [navigate]);

  return null;
}
